import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconDelete from '../../assets/icon_delete.svg';
import iconEdit from '../../assets/icon_edit.svg';
import iconEditAdmin from '../../assets/edit_admin.svg';
import AppSpinner from '../../components/AppSpinner';

interface UserData {
  _id: string;
  userId: string;
  email: string;
  displayName: string;
  role: string;
  createdAt: string;
  lastLoginDate?: string;
  status?: string;
  age?: number;
  phoneNumber?: string;
}

const AdminCounselorListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchStatus, setSearchStatus] = useState<string>('all');
  const [searchType, setSearchType] = useState<string>('all');
  const [keyword, setKeyword] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchCounselors = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/admin/counselorList', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const dataWithStatus = response.data.map((user: any) => ({
          ...user,
          status: user.status || '정상',
        }));

        setUsers(dataWithStatus);
        setFilteredUsers(dataWithStatus);
      } catch (err) {
        setError('상담사 목록을 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchCounselors();
  }, [isAuthenticated]);

  const handleSearch = () => {
    let result = [...users];

    if (searchStatus !== 'all') {
      result = result.filter(user => user.status === searchStatus);
    }

    if (keyword) {
      const lowerKeyword = keyword.toLowerCase();
      result = result.filter(user => {
        if (searchType === 'name') return user.displayName?.toLowerCase().includes(lowerKeyword);
        if (searchType === 'id') return user.userId?.toLowerCase().includes(lowerKeyword);
        if (searchType === 'email') return user.email?.toLowerCase().includes(lowerKeyword);
        return (
          user.displayName?.toLowerCase().includes(lowerKeyword) ||
          user.userId?.toLowerCase().includes(lowerKeyword) ||
          user.email?.toLowerCase().includes(lowerKeyword)
        );
      });
    }

    setFilteredUsers(result);
    setCurrentPage(1);
    setSelectedUserIds([]);
  };

  const handleReset = () => {
    setSearchStatus('all');
    setSearchType('all');
    setKeyword('');
    setFilteredUsers(users);
    setCurrentPage(1);
    setSelectedUserIds([]);
  };

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = paginatedUsers.map(user => user._id);
      setSelectedUserIds(prev => Array.from(new Set([...prev, ...allIds])));
    } else {
      const pageIds = paginatedUsers.map(user => user._id);
      setSelectedUserIds(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectUser = (id: string) => {
    setSelectedUserIds(prev =>
      prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]
    );
  };

  const isAllSelected =
    paginatedUsers.length > 0 && paginatedUsers.every(user => selectedUserIds.includes(user._id));

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;

  return (
    <div className="admin-page">
      <h1>상담사 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>상태</label>
          <select value={searchStatus} onChange={e => setSearchStatus(e.target.value)}>
            <option value="all">전체</option>
            <option value="정상">정상</option>
            <option value="정지">정지</option>
            <option value="탈퇴">탈퇴</option>
          </select>
        </div>

        <div className="filter-group">
          <label>검색</label>
          <div className="search-input-group">
            <select value={searchType} onChange={e => setSearchType(e.target.value)}>
              <option value="all">전체</option>
              <option value="name">이름</option>
              <option value="id">아이디</option>
              <option value="email">이메일</option>
            </select>
            <input
              type="text"
              placeholder="검색어를 입력하세요"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
          <button className="btn-reset" onClick={handleReset}>
            초기화
          </button>
        </div>
      </div>
      
      <div className="list-info">
        총 상담사 <span className="count">{filteredUsers.length}</span> | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table user-list-table">
          <colgroup>
            <col width="50px" />
            <col width="10%" />
            <col width="10%" />
            <col width="%" />
            <col width="5%" />
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />         
          </colgroup>
          <thead>
            <tr>
              <th>
                <label className="custom-checkbox">
                  <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                  <span className="checkmark"></span>
                </label>
              </th>
              <th>이름</th>
              <th>아이디</th>
              <th>이메일</th>
              <th>나이</th>
              <th>연락처</th>
              <th>등록일</th>
              <th>최근 접속일</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map(user => (
                <tr key={user._id}>
                  <td>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user._id)}
                        onChange={() => handleSelectUser(user._id)}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </td>
                  <td>{user.displayName}</td>
                  <td>{user.userId || '-'}</td>
                  <td>{user.email}</td>
                  <td>{user.age || '-'}</td>
                  <td>{user.phoneNumber || '-'}</td>
                  <td>
                    {new Date(user.createdAt).toLocaleDateString()}{' '}
                  </td>
                  <td>
                    {user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleString() : '-'}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${user.status === '정상' ? 'normal' : 'suspended'}`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-icon view m-auto" title="상세보기"
                      onClick={() => navigate(`/admin/users/${user._id}`)}
                    >
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="no-data">
                  등록된 상담사가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


        <div className="pagination-container">
          <div className="pagination">
            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
              <img src={leftArrow1} alt="처음" />
            </button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <img src={leftArrow2} alt="이전" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={currentPage === page ? 'active' : ''}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              <img src={rightArrow1} alt="다음" />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              <img src={rightArrow2} alt="끝" />
            </button>
          </div>

          <div className="bottom-actions">
            <button className="btn-delete-selection">
              <img src={iconDelete} />
              삭제
            </button>
            <button className="btn-register" onClick={() => navigate('/admin/users/register?role=counselor')}>
              <img src={iconEdit} />
              상담사 등록
            </button>
          </div>          
        </div>

    </div>
  );
};

export default AdminCounselorListPage;